import React from 'react';
import { Box, Typography, BoxProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ArrowRight } from 'lucide-react';

export interface ProposalCardProps extends BoxProps {
  proposal: {
    id: string;
    title: string;
    description: string;
    status: string;
  };
  onViewDetails: (id: string) => void;
}

const CardContainer = styled(Box)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  padding: theme.spacing(3),
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 30px rgba(138, 43, 226, 0.2)',
    border: '1px solid rgba(138, 43, 226, 0.4)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: 'linear-gradient(90deg, #003366, #00CC99, #8A2BE2, #FF6F00)',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover::before': {
    opacity: 1,
  },
}));

const ProposalCard: React.FC<ProposalCardProps> = ({ proposal, onViewDetails, ...props }) => {
  const handleClick = () => {
    onViewDetails(proposal.id);
  };

  return (
    <CardContainer onClick={handleClick} {...props}>
      <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
        {proposal.title}
      </Typography>
      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2, flexGrow: 1 }}>
        {proposal.description}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <Box sx={{ 
          px: 1.5, 
          py: 0.5, 
          borderRadius: 1, 
          bgcolor: proposal.status === 'active' ? 'rgba(0, 204, 153, 0.2)' : 'rgba(255, 111, 0, 0.2)',
          display: 'inline-block'
        }}>
          <Typography variant="caption" sx={{ 
            color: proposal.status === 'active' ? '#00CC99' : '#FF6F00',
            fontWeight: 'bold'
          }}>
            {proposal.status.toUpperCase()}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ 
          color: '#8A2BE2',
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover': { textDecoration: 'underline' }
        }}>
          View Details <ArrowRight size={16} style={{ marginLeft: 4 }} />
        </Typography>
      </Box>
    </CardContainer>
  );
};

export default ProposalCard; 